import { useEffect, useState } from "react";

import { useSpecificationsSnapshotByExternalIdAndSnapshotExternalIdGetV2 } from "handlers/specifications/specifications";

interface IUseSpecificationSnapshotParams {
    /** External id of the spec the viewer is showing. */
    specificationExternalId: string;
    /** Job context for the snapshot fetch. */
    jobId: number;
    /**
     * Whether the version history panel is open. When it transitions to false, the selected
     * snapshot is cleared automatically so callers don't have to remember to coordinate it.
     */
    isVersionHistoryOpen: boolean;
}

export function useSpecificationSnapshot({
    specificationExternalId,
    jobId,
    isVersionHistoryOpen,
}: IUseSpecificationSnapshotParams) {
    const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);

    // Lives here, not in SpecificationVersionHistory, so mobile's panel-swap doesn't unmount the query.
    // keepPreviousData holds the prior snapshot through transitions so title/content swap atomically.
    const { data: snapshotResponse, isLoading: isSnapshotQueryLoading } =
        useSpecificationsSnapshotByExternalIdAndSnapshotExternalIdGetV2(
            specificationExternalId,
            selectedSnapshotId ?? "",
            { jobId },
            {
                query: {
                    enabled: !!selectedSnapshotId && !!specificationExternalId,
                    keepPreviousData: true,
                },
            },
        );

    // Clear selection when the version history panel closes. Avoids every close-site needing to remember.
    useEffect(() => {
        if (!isVersionHistoryOpen) {
            setSelectedSnapshotId(null);
        }
    }, [isVersionHistoryOpen]);

    const activeSnapshotData = selectedSnapshotId ? (snapshotResponse?.data ?? null) : null;
    const isShowingSnapshotContent = !!activeSnapshotData;
    const isSnapshotLoading = !!selectedSnapshotId && isSnapshotQueryLoading;

    return {
        selectedSnapshotId,
        setSelectedSnapshotId,
        activeSnapshotData,
        isShowingSnapshotContent,
        isSnapshotLoading,
    };
}
